import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AnyTransition, StateMachineState, Transition, TransitionV2 } from '../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../context/StateMachineEditorContext'
import { isV2Definition, v2TransitionSpecificity } from '../../utils/stateMachineV2'
import { CardRow } from '../CardRow'

import { TransitionEditForm } from './TransitionEditForm'
import { TransitionEditFormV2 } from './v2/TransitionEditFormV2'
import { detectV2OverlapWarnings } from './v2/v2OverlapWarnings'
import { Badge, Button, Flex, Tooltip, Typography } from '@percent/lemonade'

export interface TransitionListPanelProps {
  selectedState: StateMachineState
  availableStates: StateMachineState[]
  onAddTransition: (stateId: string, transition: AnyTransition) => void
  onUpdateTransition: (stateId: string, index: number, transition: AnyTransition) => void
  onDeleteTransition: (stateId: string, transitionIndex: number) => void
}

export function TransitionListPanel({
  selectedState,
  availableStates,
  onAddTransition,
  onUpdateTransition,
  onDeleteTransition
}: Readonly<TransitionListPanelProps>) {
  const { t } = useTranslation()
  const { metadata, localDefinition } = useStateMachineEditorContext()
  const [editingTransitionIndex, setEditingTransitionIndex] = useState<number | null>(null)

  useEffect(() => {
    setEditingTransitionIndex(null)
  }, [selectedState.id])

  const isV2 = !!localDefinition && isV2Definition(localDefinition)

  const eventLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const opt of metadata.eventNames) {
      map.set(opt.value, opt.label)
    }

    return map
  }, [metadata.eventNames])

  const stateLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of availableStates) {
      map.set(s.id, s.metadata?.displayKey || s.id)
    }

    return map
  }, [availableStates])

  const v2Warnings = useMemo(() => {
    if (!isV2) return new Map<number, ReturnType<typeof detectV2OverlapWarnings>[number]>()
    const list = detectV2OverlapWarnings(selectedState.transitions as TransitionV2[])

    return new Map(list.map(w => [w.transitionIndex, w]))
  }, [isV2, selectedState.transitions])

  const describeTransition = useCallback(
    (tr: AnyTransition): string => {
      if (isV2) {
        const v2 = tr as TransitionV2
        const topTypes = v2.when.eventsMatching?.events.map(e => e.type) ?? []
        const dataTypes = v2.when.data?.eventsMatching?.events.map(e => e.eventType) ?? []
        const eventTypes = [...new Set([...topTypes, ...dataTypes])]

        if (eventTypes.length === 0) return t('stateMachine.sidePanel.fallThrough')

        return eventTypes.map(ty => eventLabelMap.get(ty) ?? ty).join(', ')
      }

      const v1 = tr as Transition
      const eventType = v1.when?.eventsMatching?.events?.[0]?.type

      return eventType ? eventLabelMap.get(eventType) ?? eventType : t('stateMachine.sidePanel.fallThrough')
    },
    [isV2, eventLabelMap, t]
  )

  const handleTransitionSubmit = useCallback(
    (transition: AnyTransition) => {
      if (editingTransitionIndex !== null && editingTransitionIndex >= 0) {
        onUpdateTransition(selectedState.id, editingTransitionIndex, transition)
      } else {
        onAddTransition(selectedState.id, transition)
      }
      setEditingTransitionIndex(null)
    },
    [selectedState.id, editingTransitionIndex, onAddTransition, onUpdateTransition]
  )

  const editingTransition =
    editingTransitionIndex !== null && editingTransitionIndex >= 0
      ? selectedState.transitions[editingTransitionIndex]
      : undefined

  if (editingTransitionIndex !== null) {
    if (isV2) {
      return (
        <TransitionEditFormV2
          onSubmit={handleTransitionSubmit}
          onCancel={() => setEditingTransitionIndex(null)}
          availableStates={availableStates}
          initialValues={editingTransition as TransitionV2 | undefined}
        />
      )
    }

    return (
      <TransitionEditForm
        onSubmit={handleTransitionSubmit}
        onCancel={() => setEditingTransitionIndex(null)}
        availableStates={availableStates}
        initialValues={editingTransition as Transition | undefined}
      />
    )
  }

  return (
    <Flex direction="column" flex={1} miw={0} mih={0}>
      <Flex direction="column" gap="md" flex={1} mih={0} style={{ overflowY: 'auto' }} pt={8}>
        {selectedState.transitions.map((tr, idx) => {
          const warning = v2Warnings.get(idx)
          const specificity = isV2 ? v2TransitionSpecificity(tr as TransitionV2) : null

          return (
            <CardRow
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              onClick={() => setEditingTransitionIndex(idx)}
              onRemove={() => onDeleteTransition(selectedState.id, idx)}
              removeLabel={t('button.remove')}
              removeTestId={`remove-transition-${idx}`}
              testId={`transition-item-${idx}`}
            >
              <Flex direction="column" gap="xxs">
                <Typography>{`${describeTransition(tr)} → ${stateLabelMap.get(tr.to) ?? tr.to}`}</Typography>
                {isV2 && (
                  <Flex gap="xs" align="center">
                    <Tooltip content={t('stateMachine.transitionEditV2.specificityTooltip')}>
                      <Badge variant="default" testId={`transition-specificity-${idx}`}>
                        {t('stateMachine.transitionEditV2.specificityBadge', { count: specificity ?? 0 })}
                      </Badge>
                    </Tooltip>
                    {warning?.equalSpecificity && (
                      <Tooltip
                        content={
                          warning.winnerIndex === idx
                            ? t('stateMachine.transitionEditV2.winnerIsSelfTooltip')
                            : t('stateMachine.transitionEditV2.equalSpecificityTooltip', {
                                winnerIndex: (warning.winnerIndex ?? idx) + 1
                              })
                        }
                      >
                        <Badge variant="warning" icon="warning" testId={`transition-overlap-warn-${idx}`}>
                          {t('stateMachine.transitionEditV2.equalSpecificityBadge')}
                        </Badge>
                      </Tooltip>
                    )}
                  </Flex>
                )}
              </Flex>
            </CardRow>
          )
        })}
        {selectedState.transitions.length === 0 && <Typography>{t('stateMachine.sidePanel.noTransitions')}</Typography>}
      </Flex>
      <Flex
        justify="flex-end"
        style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}
        data-testid="transition-list-footer"
      >
        <Button
          variant="secondary"
          size="small"
          onClick={() => setEditingTransitionIndex(-1)}
          data-testid="add-transition-button"
        >
          {t('stateMachine.transitionEdit.addTransition')}
        </Button>
      </Flex>
    </Flex>
  )
}
